import { disablePushNotifications } from './push';
import { resumeSession, isLogoutPending, revokeBrowserSession } from '@/core';
import { z } from 'zod';
import { createPkceHandshake } from '@/core';
import { ApiError, getSession, loadEnv, setSession, type SessionUser } from '@/core';
import { httpClient } from './httpClient';
import {
  parseEquipmentCategories,
  parseMe,
  parsePasswordCodeResponse,
  parseRegisterResponse,
  type EquipmentCategoryOption,
  type TechAuthMe,
} from './schemas/auth';

// Reexport para a camada de apresentação (CadastroView) — o tipo mora com o schema (C4).
export type { EquipmentCategoryOption } from './schemas/auth';

const TokenResponseSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.union([z.number(), z.string().datetime({ offset: true })]),
  // O BFF devolve o token CSRF no corpo: a SPA roda em outra origem (Pages) e não consegue
  // ler `document.cookie` do BFF — o valor fica só em memória (seguranca-frontend-web).
  csrf: z.string().min(1),
});

export async function beginLogin(loginHint?: string): Promise<void> {
  const env = loadEnv();
  if (isLogoutPending()) await logout();
  const handshake = await createPkceHandshake();
  sessionStorage.setItem('dt_pkce_verifier', handshake.codeVerifier);
  sessionStorage.setItem('dt_pkce_state', handshake.state);
  const params = new URLSearchParams({
    client_id: env.VITE_KEYCLOAK_CLIENT_ID,
    redirect_uri: `${window.location.origin}/auth/callback`,
    response_type: 'code',
    scope: 'openid profile email',
    state: handshake.state,
    code_challenge: handshake.codeChallenge,
    code_challenge_method: 'S256',
    // Identificador digitado (CPF ou e-mail) como dica ao Keycloak — a senha NUNCA sai da tela.
    ...(loginHint !== undefined ? { login_hint: loginHint } : {}),
  });
  window.location.assign(`${env.VITE_KEYCLOAK_BASE_URL}/realms/${env.VITE_KEYCLOAK_REALM}/protocol/openid-connect/auth?${params.toString()}`);
}

function expiresAt(value: number | string): number {
  return typeof value === 'number' ? value : Date.parse(value);
}

export async function exchangeCode(code: string, codeVerifier: string): Promise<void> {
  const env = loadEnv();
  const response = await fetch(`${env.VITE_BFF_BASE_URL}/auth/session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': readCsrfToken() ?? '',
    },
    body: JSON.stringify({ redirectUri: `${window.location.origin}/auth/callback`, persona: 'tecnicos', code, codeVerifier }),
  });
  if (!response.ok) throw new Error('Não foi possível iniciar a sessão.');
  const parsed = TokenResponseSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error('Resposta de autenticação inválida.');
  resumeSession();
  setSession({ ...getSession(), accessToken: parsed.data.accessToken, expiresAt: expiresAt(parsed.data.expiresAt), csrf: parsed.data.csrf });
}

export async function loadCurrentUser(): Promise<SessionUser> {
  const payload: unknown = await httpClient.get('/auth/me');
  let parsed: TechAuthMe;
  try {
    parsed = parseMe(payload);
  } catch {
    throw new Error('Resposta de usuário técnico inválida.');
  }
  const user: SessionUser = {
    id: parsed.id,
    name: parsed.nome,
    email: parsed.email,
    persona: 'tecnicos',
    roles: [],
  };
  setSession({ ...getSession(), user });
  return user;
}

/**
 * Fluxo de senha por código de 6 dígitos (decisão do MAPEAMENTO §7 — código por e-mail no lugar
 * da senha temporária). O envio responde sempre 200 com a mesma mensagem (anti-enumeração é
 * garantida no backend); a confirmação responde 204 e dispara a troca real no Keycloak.
 */
export type PasswordFlowMode = 'recover' | 'setup';

export async function requestPasswordCode(mode: PasswordFlowMode, email: string): Promise<void> {
  const path = mode === 'setup' ? '/auth/password/setup/resend' : '/auth/password/forgot';
  const payload: unknown = await httpClient.post<unknown>(path, { email });
  try {
    parsePasswordCodeResponse(payload);
  } catch {
    throw new Error('Resposta de envio de código inválida.');
  }
}

export async function confirmPasswordCode(mode: PasswordFlowMode, email: string, code: string, newPassword: string): Promise<void> {
  const path = mode === 'setup' ? '/auth/password/setup' : '/auth/password/reset';
  await httpClient.post<unknown>(path, { email, code, newPassword });
}

export async function logout(): Promise<void> {
  try { await disablePushNotifications(); } finally { await revokeBrowserSession(loadEnv().VITE_BFF_BASE_URL); }
}

export function readCsrfToken(): string | null {
  // Em memória, nunca via document.cookie: o cookie dt_csrf pertence ao domínio do BFF.
  return getSession().csrf;
}

/**
 * Cadastro multipart do técnico via httpClient compartilhado, endpoint público sem sessão
 * obrigatória. city e categoryIds repetidos preservam o contrato em TechAuthDtos.cs. A resposta (200) é validada por Zod e descartada: o estado
 * de sucesso da tela não consome o corpo.
 */
export interface TechnicianRegistration {
  readonly email: string;
  readonly nome: string;
  readonly documentId: string;
  readonly phone?: string;
  readonly termsAccepted: boolean;
  readonly city: string;
  readonly categoryIds: readonly number[];
  readonly photo?: File;
}

export async function registerTechnician(registration: TechnicianRegistration): Promise<void> {
  const form = new FormData();
  form.append('email', registration.email);
  form.append('nome', registration.nome);
  form.append('documentId', registration.documentId);
  form.append('termsAccepted', String(registration.termsAccepted));
  if (registration.phone !== undefined) form.append('phone', registration.phone);
  if (registration.city !== '') form.append('city', registration.city);
  for (const categoryId of registration.categoryIds) form.append('categoryIds', String(categoryId));
  if (registration.photo !== undefined) form.append('photo', registration.photo);

  const payload = await httpClient.post<unknown>('/auth/register', form);
  try {
    parseRegisterResponse(payload);
  } catch {
    throw new Error('Resposta de cadastro inválida.');
  }
}

/**
 * Catálogo de especialidades para o formulário de cadastro (GET /equipment-categories) —
 * endpoint anônimo, já liberado no BFF para a tela pública de cadastro.
 */
export async function fetchEquipmentCategoriesForRegister(): Promise<EquipmentCategoryOption[]> {
  const payload: unknown = await httpClient.get<unknown>('/equipment-categories');
  return parseEquipmentCategories(payload);
}

const CsrfResponseSchema = z.object({ csrf: z.string().min(1) });

/**
 * Rehidratação do token CSRF da sessão de navegador (GET /auth/csrf).
 *
 * Num reload a memória da SPA é zerada, mas o cookie `__Host-dt_session` continua válido: o BFF
 * injeta o Authorization e o `GET /auth/me` responde 200, então a navegação segue — só que sem
 * `csrf` em memória toda mutação recebe 403 "Token CSRF ausente ou inválido". Este endpoint
 * devolve o token da sessão corrente (401 quando não há sessão). O valor continua APENAS em
 * memória (regra #24 / seguranca-frontend-web) e a resposta é validada por Zod na fronteira.
 *
 * Idempotente: não faz nada quando já há token em memória.
 */
export async function ensureCsrfToken(): Promise<void> {
  if (getSession().csrf !== null) return;
  const env = loadEnv();
  let response: Response;
  try {
    response = await fetch(`${env.VITE_BFF_BASE_URL}/auth/csrf`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    // Rede/BFF fora do ar não é sessão inválida: o chamador precisa distinguir para não deslogar
    // quem estava logado por causa de um blip.
    throw new ApiError({
      status: 0,
      correlationId: undefined,
      kind: 'network',
      message: 'Não foi possível conectar. Verifique sua rede ou tente novamente.',
    });
  }
  if (!response.ok) {
    // 401 = sessão de navegador realmente inválida; 5xx e demais falhas são transientes.
    throw new ApiError({
      status: response.status,
      correlationId: response.headers.get('X-Correlation-Id') ?? undefined,
      kind: 'http',
      message: response.status === 401
        ? 'Sua sessão expirou. Entre novamente.'
        : 'Não foi possível validar a proteção da sessão. Tente novamente em instantes.',
    });
  }
  const parsed = CsrfResponseSchema.safeParse(await response.json().catch(() => undefined));
  if (!parsed.success) throw new Error('Resposta de proteção CSRF inválida.');
  setSession({ ...getSession(), csrf: parsed.data.csrf });
}

// Fachada C4 para a apresentação, sem dependência direta de C2.
export { isLogoutPending };
